import React, { useEffect } from "react";
import Column from "@components/column";
import Fields from "@components/fields";
import FieldWrapper from "@components/wrapper/field-wrapper";
import Typography from "@components/typography";
import Row from "@components/row";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";

interface DeliveryInfoProps {
  values: any;
  setFieldValue: (field: string, value: any) => void;
}

const DeliveryInfo: React.FC<DeliveryInfoProps> = ({
  values,
  setFieldValue,
}) => {
  const [searchParams] = useSearchParams();
  const moduleParam = searchParams.get("module")?.toLowerCase() || "";
  const isGrocery = moduleParam === "grocery";

  useEffect(() => {
    const purchaseLimit = Number(values.purchaseLimit);
    const maxTwoWheeler = Number(values.maxTwoWheelerLimit);

    // Auto-calculate 4 wheeler limit ONLY when "both" is selected
    if (values.vehicleAvailability === "both" && values.purchaseLimit && values.maxTwoWheelerLimit) {
      const fourWheelerLimit = Math.max(0, purchaseLimit - maxTwoWheeler);
      if (Number(values.maxFourWheelerLimit) !== fourWheelerLimit) {
        setFieldValue("maxFourWheelerLimit", fourWheelerLimit.toString());
      }
    } else if (values.vehicleAvailability !== "both") {
      // Clear 4-wheeler limit when not "both"
      if (values.vehicleAvailability === "two-wheeler" && values.maxFourWheelerLimit !== "") {
        setFieldValue("maxFourWheelerLimit", "");
      }
      // Clear 2-wheeler limit when only four-wheeler
      if (values.vehicleAvailability === "four-wheeler" && values.maxTwoWheelerLimit !== "") {
        setFieldValue("maxTwoWheelerLimit", "");
      }
    }
  }, [
    values.vehicleAvailability,
    values.purchaseLimit,
    values.maxTwoWheelerLimit,
    values.maxFourWheelerLimit,
    isGrocery,
    setFieldValue,
  ]);

  const vehicleOptions = [
    { id: "two-wheeler", title: "Two Wheeler" },
    { id: "four-wheeler", title: "Four Wheeler" },
    { id: "both", title: "Both" },
  ];

  return (
    <FieldWrapper>
      <Column>
        <Typography weight={600} size={18}>
          Delivery Information
        </Typography>
        <Row styleRow="align-items:stretch; gap: 20px;">
          <div
            style={{
              flex: 1,
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              padding: "16px",
            }}
          >
            <Column stylecolumn="row-gap: 20px;">
              <Fields.GroupRadioBox
                name="deliveryAvailability"
                label="Delivery Availability"
                options={[
                  { id: "instant", title: "Instant" },
                  { id: "regular", title: "Regular" },
                ]}
                titleAsKey="title"
                valueAsKey="id"
              />
              <Fields.Input
                name="estimatedDeliveryDay"
                label="Estimated Delivery Days"
                placeholder="e.g. 2"
                type="number"
                disabled={values.deliveryAvailability === "instant"}
              />
            </Column>
          </div>

          <div
            style={{
              flex: 1,
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              padding: "16px",
            }}
          >
            <Column stylecolumn="row-gap: 20px;">
              <Fields.GroupRadioBox
                name="vehicleAvailability"
                label="Vehicle Availability"
                options={vehicleOptions}
                titleAsKey="title"
                valueAsKey="id"
              />
              <Row styleRow="gap: 20px;">
                {(values.vehicleAvailability === "two-wheeler" || values.vehicleAvailability === "both") && (
                  <Fields.Input
                    name="maxTwoWheelerLimit"
                    label="Limit for 2 wheeler"
                    placeholder="Enter limit"
                  />
                )}
                {(values.vehicleAvailability === "four-wheeler" || values.vehicleAvailability === "both") && (
                  <Fields.Input
                    name="maxFourWheelerLimit"
                    label="Limit for 4 wheeler"
                    placeholder={values.vehicleAvailability === "both" ? "Calculated limit" : "Enter limit"}
                    disabled={values.vehicleAvailability === "both"}
                  />
                )}
              </Row>
            </Column>
          </div>
        </Row>
        {values.vehicleAvailability === "both" && (
          <div style={{ marginTop: "20px" }}>
            <Typography styleText="font-size: 14px; color: #666; font-style: italic;">
              * Note: The Limit for 4 wheeler is automatically calculated by
              subtracting the Limit for 2 wheeler from the Maximum Purchase Limit.
            </Typography>
          </div>
        )}
      </Column>
    </FieldWrapper>
  );
};

export default DeliveryInfo;
